import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import "./ChatStorage.scss";

import PhotoDetails from "../PhotoDetails/PhotoDetails";
import { getAccessToken } from "../../awsAuth";
import { formatDuration, formatFileSize } from "./chatV2";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";
const PAGE = 30;

// "Медіа сховище": photos + videos ("Медіа") and documents ("Файли") of the whole conversation — from BOTH people,
// newest first, loaded page by page. It uses the SAME endpoint as the mobile app (GET /api/messages/chat/media).
// It used to render nothing at all: the request carried no Authorization header, the API answered 401 and the
// component swallowed that as "no images".
const ChatStorage = ({ threadId, onClose }) => {
  const [tab, setTab] = useState("media");
  const [items, setItems] = useState({ media: [], files: [] });
  const [hasMore, setHasMore] = useState({ media: false, files: false });
  const [cursors, setCursors] = useState({ media: null, files: null });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [isClosing, setIsClosing] = useState(false);
  const [loadedMap, setLoadedMap] = useState(() => new Map());
  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [videoSrc, setVideoSrc] = useState(null);
  const loadedTabs = useRef({ media: false, files: false });
  const busy = useRef(false);

  const authFetch = useCallback(async (path) => {
    const token = await getAccessToken().catch(() => null);
    return fetch(`${API_BASE}${path}`, {
      cache: "no-store",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  }, []);

  const loadPage = useCallback(
    async (which, reset, cursor) => {
      if (!threadId || busy.current) return;
      busy.current = true;
      if (reset) setLoading(true);
      else setLoadingMore(true);
      setError("");

      try {
        const qs = new URLSearchParams({ threadId, kind: which, limit: String(PAGE) });
        if (!reset && cursor) qs.set("cursor", String(cursor));
        const res = await authFetch(`/api/messages/chat/media?${qs.toString()}`);
        if (!res.ok) throw new Error("Не вдалося завантажити медіа");
        const data = await res.json();
        const list = Array.isArray(data.items) ? data.items : [];

        loadedTabs.current[which] = true;
        setItems((prev) => ({ ...prev, [which]: reset ? list : [...prev[which], ...list] }));
        setHasMore((prev) => ({ ...prev, [which]: !!data.hasMore }));
        setCursors((prev) => ({ ...prev, [which]: data.nextCursor || null }));
        if (reset) setLoadedMap(new Map());
      } catch (e) {
        setError(e.message || "Не вдалося завантажити медіа");
      } finally {
        busy.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [threadId, authFetch]
  );

  useEffect(() => {
    loadPage("media", true);
  }, [loadPage]);

  const switchTab = (next) => {
    if (next === tab) return;
    setTab(next);
    if (!loadedTabs.current[next]) loadPage(next, true);
  };

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, []);

  const handleRequestClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose?.();
    }, 240);
  }, [isClosing, onClose]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") handleRequestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleRequestClose]);

  const overlayClasses = useMemo(
    () => ["storage-modal", "storage-modal__overlay", isClosing ? "is-closing" : "is-open"].join(" "),
    [isClosing]
  );

  const photos = useMemo(
    () =>
      items.media
        .filter((m) => m.type === "image")
        .map((m) => ({ src: m.mediaUrl || m.url || m.previewUrl || "" })),
    [items.media]
  );

  const markLoaded = useCallback((key) => {
    setLoadedMap((m) => {
      if (m.get(key)) return m;
      const next = new Map(m);
      next.set(key, true);
      return next;
    });
  }, []);

  const openMedia = (e, item) => {
    e.preventDefault();
    if (item.type === "video") {
      if (item.url || item.mediaUrl) setVideoSrc(item.url || item.mediaUrl);
      return;
    }
    const src = item.mediaUrl || item.url || item.previewUrl || "";
    const idx = photos.findIndex((p) => p.src === src);
    setPhotoIndex(idx >= 0 ? idx : 0);
    setPhotoOpen(true);
  };

  const openFile = async (item) => {
    try {
      const res = await authFetch(
        `/api/messages/chat/attachment?threadId=${encodeURIComponent(threadId)}&createdAt=${encodeURIComponent(item.createdAt)}`
      );
      if (!res.ok) throw new Error("Не вдалося відкрити файл");
      const data = await res.json();
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError(e.message || "Не вдалося відкрити файл");
    }
  };

  const stop = (e) => e.stopPropagation();
  const current = items[tab];

  return (
    <div className={overlayClasses} onClick={handleRequestClose} role="dialog" aria-modal="true" aria-label="Медіа сховище">
      <div className="storage-modal__inner" onClick={stop} role="document">
        <div className="storage-modal__top">
          <div className="title">Медіа сховище</div>
          <button className="close" onClick={handleRequestClose} type="button" aria-label="Закрити">
            ✕
          </button>
        </div>

        <div className="storage-tabs" role="tablist">
          {[
            ["media", "Медіа"],
            ["files", "Файли"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className={`storage-tab ${tab === key ? "is-active" : ""}`}
              onClick={() => switchTab(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {error && <div className="storage-empty">{error}</div>}

        {loading && !error && <div className="storage-loading">Завантаження…</div>}

        {!loading && !error && current.length === 0 && (
          <div className="storage-empty">
            {tab === "media" ? "У цьому чаті ще немає фото чи відео" : "У цьому чаті ще немає файлів"}
          </div>
        )}

        {!loading && tab === "media" && current.length > 0 && (
          <div className="storage-grid is-ready">
            {current.map((m, idx) => {
              const src = m.thumbnailUrl || m.previewUrl || m.mediaUrl || m.url || "";
              const key = m.id ?? `${src}-${idx}`;
              return (
                <a
                  key={key}
                  className={["storage-item", loadedMap.get(key) ? "is-loaded" : ""].join(" ")}
                  href={m.mediaUrl || m.url || src}
                  target="_blank"
                  rel="noreferrer"
                  style={{ animationDelay: `${Math.min(idx * 30, 300)}ms` }}
                  onClick={(e) => openMedia(e, m)}
                >
                  {src ? (
                    <img src={src} alt="" loading="lazy" onLoad={() => markLoaded(key)} onError={() => markLoaded(key)} />
                  ) : (
                    <span className="storage-item__fallback" />
                  )}
                  {m.type === "video" && (
                    <>
                      <span className="storage-item__play" aria-hidden="true">▶</span>
                      {!!m.duration && <span className="storage-item__duration">{formatDuration(m.duration)}</span>}
                    </>
                  )}
                </a>
              );
            })}
          </div>
        )}

        {!loading && tab === "files" && current.length > 0 && (
          <div className="storage-files">
            {current.map((f, idx) => (
              <button key={f.id ?? idx} type="button" className="storage-file" onClick={() => openFile(f)}>
                <span className="storage-file__icon" aria-hidden="true">📄</span>
                <span className="storage-file__text">
                  <span className="storage-file__name">{f.filename || "Файл"}</span>
                  <span className="storage-file__meta">
                    {[formatFileSize(f.size), new Date(f.createdAt).toLocaleDateString()].filter(Boolean).join(" · ")}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}

        {!loading && hasMore[tab] && (
          <button
            type="button"
            className="storage-more"
            disabled={loadingMore}
            onClick={() => loadPage(tab, false, cursors[tab])}
          >
            {loadingMore ? "Завантаження…" : "Показати більше"}
          </button>
        )}
      </div>

      <PhotoDetails open={photoOpen} photos={photos} startIndex={photoIndex} onClose={() => setPhotoOpen(false)} />

      {videoSrc && (
        <div className="chat-video-viewer" onClick={() => setVideoSrc(null)} role="dialog" aria-label="Відео">
          <button type="button" className="chat-video-viewer__close" onClick={() => setVideoSrc(null)} aria-label="Закрити">
            ×
          </button>
          <video src={videoSrc} controls autoPlay playsInline onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default ChatStorage;
